/**
 * T-050: sales_outbox health monitor + bounded force-drain.
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'react-toastify';
import { useRBAC } from '@/contexts/RBACContext';
import { getOutboxHealth, forceDrainOutbox } from '@/lib/pocketSettingsApi';
import type { OutboxHealthStatus } from '@/types/pocketSettings';

const PENDING_WARN_THRESHOLD = 50;

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function OutboxHealthCard() {
  const { isSuperadmin } = useRBAC();
  const [status, setStatus] = useState<OutboxHealthStatus | null>(null);
  const [draining, setDraining] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    getOutboxHealth()
      .then((data) => {
        if (!cancelled) setStatus(data);
      })
      .catch(() => {
        // Non-fatal.
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  async function handleDrain() {
    setDraining(true);
    try {
      const result = await forceDrainOutbox();
      const s = result.summary;
      toast.success(
        `Drained ${s.drained}: ${s.delivered} delivered (${s.applied} applied, ${s.duplicate} duplicate), ${s.failed_permanent} dead-lettered.`,
      );
      setConfirmOpen(false);
      refresh();
    } catch {
      toast.error('Force drain failed.');
    } finally {
      setDraining(false);
    }
  }

  const pendingTone = (status?.pending ?? 0) > PENDING_WARN_THRESHOLD ? 'warn' : 'ok';
  const failedTone = (status?.failed ?? 0) > 0 ? 'warn' : 'ok';

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <CardTitle className="text-base font-semibold">Sales outbox health</CardTitle>
        {isSuperadmin ? (
          <Button
            size="sm"
            variant="destructive"
            onClick={() => setConfirmOpen(true)}
            disabled={draining}
          >
            Force drain
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          App→Sales sync queue. <span className="text-foreground font-medium">Pending</span> = waiting.
          <span className="text-foreground font-medium"> Failed (DLQ)</span> = dead-lettered after 8 tries.
          <span className="text-foreground font-medium"> Delivered (24h)</span> = successful pushes. Failed
          should be 0 at steady state.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Tile
            label="Pending"
            value={status ? String(status.pending) : '—'}
            tone={pendingTone}
          />
          <Tile
            label="Failed (DLQ)"
            value={status ? String(status.failed) : '—'}
            tone={failedTone}
          />
          <Tile
            label="Delivered (24h)"
            value={status ? String(status.delivered_24h) : '—'}
          />
          <Tile
            label="Oldest pending"
            value={status?.oldest_pending_at ? formatDate(status.oldest_pending_at) : '—'}
          />
        </div>

        <details>
          <summary className="text-sm font-medium cursor-pointer text-muted-foreground hover:text-foreground">
            Recent failures (top 10 dead-lettered)
          </summary>
          <div className="mt-3">
            {!status || status.recent_failures.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No dead-lettered rows.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Endpoint</TableHead>
                    <TableHead>Attempts</TableHead>
                    <TableHead>When</TableHead>
                    <TableHead>Last error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {status.recent_failures.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono text-xs">{row.endpoint}</TableCell>
                      <TableCell className="tabular-nums">{row.attempts}</TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">
                        {formatDate(row.failed_permanently_at)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-md truncate">
                        {row.last_error || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </details>

        <p className="mt-4 text-xs text-muted-foreground">
          Force drain reuses the same predicate as the cron-driven drain (no new
          destructive primitive). Audit row emitted on every invocation.
        </p>
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Force-drain sales_outbox?</DialogTitle>
            <DialogDescription>
              Runs the existing drain worker once with the default batch size (25
              rows). Same code path as the cron, with each row transitioning to
              delivered, retried, or dead-lettered. Audited.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={draining}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDrain} disabled={draining}>
              {draining ? 'Draining…' : 'Drain'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'ok' | 'warn';
}) {
  return (
    <div className="rounded-md border border-border/50 p-4 bg-card">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-muted-foreground">{label}</div>
        {tone === 'warn' ? (
          <Badge variant="outline" className="border-destructive/40 text-destructive">
            warn
          </Badge>
        ) : null}
      </div>
      <div
        className={
          'text-2xl font-semibold tabular-nums mt-1 ' +
          (tone === 'warn' ? 'text-destructive' : '')
        }
      >
        {value}
      </div>
    </div>
  );
}
